import React, { useEffect, useContext } from "react";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./App.css";
import { LanguageContext } from "./context/LanguageContext";
import { SOCIAL_LOGIN_CONFIG } from "./config/socialLogin";
import Home from "./pages/Home";
import IndianLaw from "./components/IndianLaw";
import UnitedKingdomLaw from "./components/UnitedKingdomLaw";
import AmericanLaw from "./components/AmericanLaw";
import SaudiLaw from "./components/SaudiLaw";
import LegalAdvisors from "./components/LegalAdvisors";
import Login from "./components/Login";
import Signup from "./components/Signup";
import ForgotPassword from "./components/ForgotPassword";
import Constitution from "./pages/Constitution";
import NewsAndInfo from "./pages/NewsAndInfo";
import Profile from "./pages/Profile";
import FullContent from "./pages/FullContent";
import ComplaintDraftsDisplay from "./pages/ComplaintDraftsDisplay";
import CourtDraftsDisplay from "./pages/CourtDraftsDisplay";
import UnifiedDraftsDisplay from "./pages/UnifiedDraftsDisplay";
import Articles from "./pages/Articles";
import LawRoutes from "./LawRoutes";
import PartsDisplayPage from "./pages/PartsDisplayPage";
import ArticleDisplayPage from "./pages/ArticleDisplayPage";
import FeedbackForm from "./components/FeedbackForm";
import LegalAdvisorDetails from "./pages/LegalAdvisorDetails";
import VideoConsultationRoom from "./pages/VideoConsultationRoom";
import SplashScreen from "./components/SplashScreen";
import Navbar from "./components/Navbar";
import About from "./pages/About";
import Transliterate from "./Transliterate";
import Search from "./pages/Search";
import Services from "./pages/Services";
import Pricing from "./pages/Pricing";
import Contribute from "./pages/Contribute";
import DraftCreatePage from "./pages/DraftCreatePage";
import DocumentReader from "./pages/DocumentReader";
import BecomeLegalAdvisor from "./pages/BecomeLegalAdvisor";
import SuggestedLegalAdvisors from "./pages/SuggestedLegalAdvisors";
import OtherResults from "./pages/OtherResults";
import FacebookDebug from "./components/FacebookDebug";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import InstallPWA from "./components/InstallPWA";
import PWAUpdatePrompt from "./components/PWAUpdatePrompt";
import { RealtimeProvider } from "./context/RealtimeContext";
import IncomingCallModal from "./components/consultation/IncomingCallModal";
import ActiveCallButton from "./components/consultation/ActiveCallButton";

// Import Form Pages
import CourtFormPage from "./pages/forms/CourtFormPage";
import PoliceFormPage from "./pages/forms/PoliceFormPage";
import DepartmentFormPage from "./pages/forms/DepartmentFormPage";
import BailFormPage from "./pages/forms/BailFormPage";
import WillFormPage from "./pages/forms/WillFormPage";
import PropertyFormPage from "./pages/forms/PropertyFormPage";
import ConsumerFormPage from "./pages/forms/ConsumerFormPage";
import LegalNoticeFormPage from "./pages/forms/LegalNoticeFormPage";
import AgreementFormPage from "./pages/forms/AgreementFormPage";
import ApplicationFormPage from "./pages/forms/ApplicationFormPage";
import AffidavitFormPage from "./pages/forms/AffidavitFormPage";

import "./styles/Translate.css";
import { setupTokenRefresh } from './Token/tokenManager';

const ReloadToServices = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only check on initial mount, not on every route change
    const navEntry = performance.getEntriesByType("navigation")[0];
    const isReload = navEntry && navEntry.type === "reload";

    if (isReload && location.pathname !== "/services" && !location.pathname.startsWith("/video-consultation")) {
      navigate("/services", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array = run only once on mount

  return null;
};

function App() {
  const { selectedLanguage, setSelectedLanguage } = useContext(LanguageContext);
  const [showSplash, setShowSplash] = React.useState(true);
  const [userInput, setUserInput] = React.useState("");
  const [auth, setAuth] = React.useState(localStorage.getItem('token'));
  const location = useLocation();

  // Re-check authentication when route changes (e.g., after login)
  useEffect(() => {
    setAuth(localStorage.getItem('token'));
  }, [location]);

  // Load Google Translate script and initialize when the target element exists
  useEffect(() => {
    function initTranslateIfReady() {
      const target = document.getElementById("google_translate_element");
      if (window.google && window.google.translate && target && !window._gtInitialized) {
        try {
          new window.google.translate.TranslateElement({ pageLanguage: "en" }, "google_translate_element");
          window._gtInitialized = true;
        } catch (_) {}
      }
    }

    if (!document.getElementById("google-translate-script")) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    }

    // Callback used by Google’s script
    window.googleTranslateElementInit = initTranslateIfReady;

    // Try until the Navbar (with the target element) is mounted
    const observer = new MutationObserver(initTranslateIfReady);
    observer.observe(document.body, { childList: true, subtree: true });
    const interval = setInterval(initTranslateIfReady, 800);

    return () => { observer.disconnect(); clearInterval(interval); };
  }, []);

  // Sync Google Translate dropdown with selectedLanguage
  useEffect(() => {
    const interval = setInterval(() => {
      const select = document.querySelector(".goog-te-combo");
      if (select && select.value && select.value !== selectedLanguage) {
        setSelectedLanguage(select.value);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedLanguage, setSelectedLanguage]);

  // Handle splash screen
  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Initialize token refresh mechanism
    setupTokenRefresh();

    // Cleanup on unmount
    return () => {
      clearInterval(window.refreshTimer);
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId={SOCIAL_LOGIN_CONFIG.GOOGLE_CLIENT_ID}>
      {/* PWA Components */}
      <InstallPWA />
      <PWAUpdatePrompt />
      
      {showSplash ? (
        <SplashScreen />
      ) : (
        <RealtimeProvider>
        <Navbar>
          <ReloadToServices />
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/constitution" element={<Constitution />} />
            <Route path="/news-information" element={<NewsAndInfo />} />
            <Route path="/legal-advisors" element={<LegalAdvisors />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/article-display" element={<ArticleDisplayPage />} />
            <Route path="/parts-display" element={<PartsDisplayPage />} />
            <Route path="/full-content" element={<FullContent />} />
            <Route path="/complaint-drafts" element={<UnifiedDraftsDisplay />} />
            <Route path="/court-drafts" element={<UnifiedDraftsDisplay />} />
            <Route path="/drafts" element={<UnifiedDraftsDisplay />} />
            <Route path="/indian-law" element={<IndianLaw />} />
            <Route path="/uk-law" element={<UnitedKingdomLaw />} />
            <Route path="/us-law" element={<AmericanLaw />} />
            <Route path="/saudi-law" element={<SaudiLaw />} />
            <Route path="/other-results" element={<OtherResults />} />
            <Route path="/become-legal-advisor" element={<BecomeLegalAdvisor />} />
            <Route path="/suggested-legal-advisors" element={<SuggestedLegalAdvisors />} />
            <Route path="/feedback" element={
              <>
                <FeedbackForm />
                <Transliterate
                  language_script={selectedLanguage}
                  onTextChange={setUserInput}
                />
              </>
            } />
            <Route path="/legal-advisors/:id" element={<LegalAdvisorDetails />} />
            <Route path="/video-consultation/:bookingId" element={auth ? <VideoConsultationRoom /> : <Navigate to="/login" />} />
            <Route path="/*" element={<LawRoutes />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route
              path="/search"
              element={
                auth ? (
                  <>
                    <Search />
                    <Transliterate
                      language_script={selectedLanguage}
                      onTextChange={setUserInput}
                    />
                  </>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route path="/transliterate" element={
              <Transliterate
                language_script={selectedLanguage}
                onTextChange={setUserInput}
              />
            } />
            <Route path="/draft-create" element={auth ? <DraftCreatePage /> : <Navigate to="/login" />} />
            <Route path="/document-reader" element={auth ? <DocumentReader /> : <Navigate to="/login" />} />
            <Route path="/contribute" element={auth ? <Contribute /> : <Navigate to="/login" />} />
            
            {/* Form Pages Routes */}
            <Route path="/forms/court" element={<CourtFormPage />} />
            <Route path="/forms/police" element={<PoliceFormPage />} />
            <Route path="/forms/department" element={<DepartmentFormPage />} />
            <Route path="/forms/bail" element={<BailFormPage />} />
            <Route path="/forms/will" element={<WillFormPage />} />
            <Route path="/forms/property" element={<PropertyFormPage />} />
            <Route path="/forms/consumer" element={<ConsumerFormPage />} />
            <Route path="/forms/legal-notice" element={<LegalNoticeFormPage />} />
            <Route path="/forms/agreement" element={<AgreementFormPage />} />
            <Route path="/forms/application" element={<ApplicationFormPage />} />
            <Route path="/forms/affidavit" element={<AffidavitFormPage />} />
            
            <Route path="/facebook-debug" element={<FacebookDebug />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Routes>
          <Footer />

          {/* Global video-consultation UI */}
          <ActiveCallButton />
          <IncomingCallModal />

          {/* Toast Container at app level for notifications across all pages */}
          <ToastContainer />
        </Navbar>
        </RealtimeProvider>
      )}
    </GoogleOAuthProvider>
  );
}

export default App;


